/* eslint-disable max-lines */
import {
  JupiterTheme,
  useUndoRedoValue,
} from '@jupiterone/jupiter-web-toolkit';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useBooleanState } from 'react-use-object-state';
import { GraphViewerAppProps } from '../GraphViewerApp';
import EntityContextMenu from './EntityContextMenu/EntityContextMenu';
import FilterPanel from './FilterPanel/FilterPanel';
import GraphInterface from './GraphInterface';
import GraphSearch from './GraphSearch/GraphSearch';
import GraphViewerConsole from './GraphViewerConsole/GraphViewerConsole';
import GraphViewerEntityDrawer from './GraphViewerEntityDrawer/GraphViewerEntityDrawer';
import GraphViewOptions from './GraphViewOptions/GraphViewOptions';
import { Pack } from './reducer/packVertices';
import reduceGraphData from './reducer/reduceGraphData';
import {
  GraphData,
  GraphEdge,
  GraphFilters,
  GraphVertex,
  GraphViewMode,
  GraphViewType,
  NodePositionMap,
  SelectedNode,
} from './types';
import { dedupeEdgesAndVertices } from './utils/dedupe';
import getUnrelatedNodes from './utils/getUnrelatedNodes';
import { InModal } from './utils/InModal';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    root: {
      position: 'relative',
      backgroundColor: theme.palette.background.default,
      backgroundImage: `url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0OCA0OCI+PGNpcmNsZSBmaWxsPSIjRTdFN0U3IiBjeD0iMjQiIGN5PSIyNCIgcj0iNSIvPjwvc3ZnPg==)`,
      backgroundSize: 16,
      width: '100%',
      minWidth: 200,
      height: '100%',
      overflow: 'hidden',

      '&[data-view-mode="max"]': {
        position: 'fixed',
        width: '100%',
        height: '100%',
        maxHeight: '100%',
        top: 0,
        left: 0,
        zIndex: theme.zIndex.modal,
      },
    },
    quickSearchContainer: {
      position: 'absolute',
      top: theme.spacing(3),
      left: theme.spacing(3),
      maxWidth: 250,
    },
  })
);

interface Props extends GraphViewerAppProps {
  graphType?: GraphViewType;
  nodePositions?: NodePositionMap;
  onUpdateNodePositions?: (nodes: SelectedNode[], clear?: boolean) => void;
  initiallySelectedNode?: GraphVertex;
  onUpdateGraphType: (type: GraphViewType) => void;
}

const GraphViewerContainer = ({
  vertices: initialVertices,
  edges: initialEdges = [],
  nodePositions,
  onUpdateNodePositions,
  initiallySelectedNode,
  graphType = GraphViewType.mesh,
  onUpdateGraph,
  onUpdateGraphType,
  paginationProps,
}: Props) => {
  const classes = useStyles();
  const consoleOpen = useBooleanState(false);
  const [viewMode, setViewMode] = useState<GraphViewMode>('embed');
  const [fetchedNeighborData, setNewFetchedNeighborData] = useState<GraphData>({
    vertices: [],
    edges: [],
  });
  const [reducedGraphData, setReducedGraphData] = useState<GraphData>({
    vertices: [],
    edges: [],
  });
  const [allPacks, setAllPacks] = useState<Pack[]>([]);
  const moveAllConnectedNodes = useBooleanState(false);
  const entityDrawerOpen = useBooleanState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const {
    value: filters,
    updateValue: setFilters,
    undo,
    redo,
  } = useUndoRedoValue<GraphFilters>({
    hiddenAccounts: [],
    hiddenTypes: [],
    hiddenNodes: [],
    properties: {},
    unpackedPacks: [],
  });
  const filtersOpen = useBooleanState(false);

  const allData = useMemo(() => {
    return {
      vertices: [...initialVertices, ...fetchedNeighborData.vertices],
      edges: [...initialEdges, ...fetchedNeighborData.edges],
    };
  }, [fetchedNeighborData, initialEdges, initialVertices]);

  // Inspected node is  shown in graph viewer and persists even when there is no node selected
  const [inspectedNodeId, setInspectedNodeId] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<SelectedNode>({
    id: null,
    position: [0, 0],
  });
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [
    nodeIdsThatHaveFetchedNeighbors,
    setNodeIdsThatHaveFetchedNeighbors,
  ] = useState<string[]>([]);

  const handleLoadMore = (
    loadedId: string,
    newData: { vertices: GraphVertex[]; edges: GraphEdge[] }
  ) => {
    setNodeIdsThatHaveFetchedNeighbors([
      ...nodeIdsThatHaveFetchedNeighbors,
      loadedId,
    ]);

    const {
      vertices: dedupedVertices,
      edges: dedupedEdges,
    }: GraphData = dedupeEdgesAndVertices(newData, allData);

    setNewFetchedNeighborData({
      vertices: [...fetchedNeighborData.vertices, ...dedupedVertices],
      edges: [...fetchedNeighborData.edges, ...dedupedEdges],
    });
  };

  useEffect(() => {
    const result = reduceGraphData({
      ...allData,
      filters,
    });
    setReducedGraphData({ vertices: result.vertices, edges: result.edges });
    setAllPacks(result.allPacks);

    if (
      selectedNode &&
      !result.vertices.find((v) => v.id === selectedNode.id)
    ) {
      setSelectedNode({ id: null, position: [0, 0] });
    }
  }, [
    fetchedNeighborData,
    initialVertices,
    initialEdges,
    initiallySelectedNode,
    filters,
  ]);

  const togglePack = (packId: string) => {
    let newUnpackedPacks: string[];
    if (filters.unpackedPacks.indexOf(packId) >= 0) {
      newUnpackedPacks = filters.unpackedPacks.filter((p) => p !== packId);
    } else {
      newUnpackedPacks = [...filters.unpackedPacks, packId];
    }
    setFilters({ ...filters, unpackedPacks: newUnpackedPacks });
    setSelectedNode({ id: null, position: [0, 0] });
  };

  const hideUnrelated = useCallback(
    (id: string) => {
      const unrelated = getUnrelatedNodes(id, allData);
      setFilters((prevFilters) => ({
        ...prevFilters,
        hiddenNodes: [...prevFilters.hiddenNodes, ...unrelated],
      }));
    },
    [allData]
  );

  const hideNode = useCallback(
    (id: string) =>
      setFilters((prevFilters) => ({
        ...prevFilters,
        hiddenNodes: [...prevFilters.hiddenNodes, id],
      })),
    [allData]
  );

  const handleSelectEdge = (edgeId: string | null) => {
    setSelectedEdgeId(edgeId);
    if (edgeId) {
      entityDrawerOpen.setTrue();
    }
  };

  const handleSelectNode = (node: SelectedNode) => {
    setSelectedNode(node);
    setInspectedNodeId(node.id);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (
      e.key === '`' &&
      document.activeElement?.tagName === 'DIV' &&
      containerRef.current?.contains(document.activeElement)
    ) {
      consoleOpen.toggle();
    }
  };

  const handleOnLoadMore = () => {
    paginationProps?.loadMore?.();
  };

  return (
    <InModal renderInModal={viewMode === 'max'}>
      <div
        className={classes.root}
        data-view-mode={viewMode}
        ref={containerRef}
        onKeyDown={handleKeyDown}
      >
        <GraphViewerConsole
          open={consoleOpen.state}
          onUpdateNodePositions={onUpdateNodePositions}
          allData={allData}
          reducedData={reducedGraphData}
        />
        <GraphInterface
          vertices={reducedGraphData.vertices}
          edges={reducedGraphData.edges}
          nodePositions={nodePositions}
          selectedEdgeId={selectedEdgeId}
          selectedNodeId={selectedNode.id}
          onSelectEdge={handleSelectEdge}
          onSelectNode={handleSelectNode}
          graphType={graphType}
          onUpdateNodePositions={onUpdateNodePositions}
          moveAllConnectedNodes={moveAllConnectedNodes.state}
        />
        {selectedNode.id && (
          <EntityContextMenu
            graphData={allData}
            selectedNode={selectedNode}
            onLoadMore={handleLoadMore}
            packs={allPacks}
            onTogglePack={togglePack}
            nodeIdsThatHaveFetchedNeighbors={nodeIdsThatHaveFetchedNeighbors}
            onHideNode={hideNode}
            onHideUnrelated={hideUnrelated}
            inspectedNodeId={inspectedNodeId}
            onInspect={(id: string) => {
              setInspectedNodeId(id);
              entityDrawerOpen.setTrue();
            }}
          />
        )}
        <FilterPanel
          vertices={allData.vertices}
          filters={filters}
          onChange={setFilters}
          open={filtersOpen.state}
          onClose={filtersOpen.setFalse}
        />
        <div className={classes.quickSearchContainer}>
          <GraphSearch
            reducedVertices={reducedGraphData.vertices}
            selectedNodeId={selectedNode.id}
            onSelectNode={setSelectedNode}
          />
        </div>
        <GraphViewOptions
          containerRef={containerRef}
          filters={filters}
          onChangeFilters={setFilters}
          onToggleFiltersVisible={filtersOpen.toggle}
          viewMode={viewMode}
          setViewMode={setViewMode}
          graphType={graphType}
          onUpdateGraphType={onUpdateGraphType}
          undo={undo}
          redo={redo}
          onToggleMoveAllConnectedNodes={moveAllConnectedNodes.setState}
          canLoadMore={paginationProps?.hasNextPage || false}
          loadingMore={paginationProps?.loading || false}
          onLoadMore={handleOnLoadMore}
          totalResultCount={paginationProps?.totalResultCount}
          loadedResultCount={allData.vertices.length + allData.edges.length}
        />
        {entityDrawerOpen.state && (
          <GraphViewerEntityDrawer
            graphData={allData}
            allPacks={allPacks}
            selectedNodeId={inspectedNodeId}
            selectedEdgeId={selectedEdgeId}
            open={entityDrawerOpen.state}
            onClose={entityDrawerOpen.setFalse}
            filters={filters}
            onChangeFilters={setFilters}
            onUpdateGraph={onUpdateGraph}
          />
        )}
      </div>
    </InModal>
  );
};

export default GraphViewerContainer;
